import { useMemo, useState } from "react";
import { rtctApi } from "services/rtctApi";
import { onCellular } from "utils/radarDiagnostics";
import {
  ALL_DAYS,
  TASK_NAMES,
  WEEKDAYS,
  dailyText,
  describe,
  localDaily,
  parseSched,
  parseTasks,
  schedProblem,
} from "utils/nodeSchedule";

// Schedule on the node page: how often the node measures and uploads (the
// keys of its own radio), whether interval wakes sit on the clock
// (wake_align), and its timed entries (sched, firmware 0.3.7) edited in
// node-local time and sent in UTC. One-off entries (satellite passes) are
// kept as they are. Nothing is sent until Apply, and then only the keys that
// changed, through the settings endpoint, which checks everything again.
export const MEASURE_PRESETS = [300, 600, 900, 1800, 3600, 7200];
export const UPLOAD_PRESETS = [0, 1800, 3600, 7200, 21600, 43200];
const ON_ITS_WAY = ["queued", "sent", "acknowledged"];
const EDIT_TASKS = ["p", "g", "b", "u", "k"];

export const presetText = (s) =>
  s === 0 ? "every wake" : s < 3600 ? `${s / 60} min` : `${s / 3600} h`;

// The value the node runs or is about to: a request on its way wins.
export function currentValue(settings, key) {
  const desired = settings?.desired || {};
  if (ON_ITS_WAY.includes(settings?.status) && key in desired) return desired[key];
  return settings?.reported?.[key];
}

const minuteText = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

function rowsFrom(sched, tzHours) {
  let entries = [];
  try {
    entries = parseSched(typeof sched === "string" ? sched : "");
  } catch {
    entries = [];
  }
  return {
    rows: entries
      .filter((e) => "min" in e)
      .map((e, i) => ({ id: i, ...localDaily(e, tzHours), tasks: new Set(e.tasks) })),
    oneOffs: entries.filter((e) => "at" in e),
  };
}

export default function ScheduleEditor({ mac, settings, sample, tzHours = 8, onApplied, now = Date.now() }) {
  const cellular = onCellular(sample?.raw);
  const keys = cellular
    ? { measure: "lte_interval_s", upload: "lte_send_every_s" }
    : { measure: "interval_sec", upload: "send_every_s" };
  const current = useMemo(
    () => ({
      measure: currentValue(settings, keys.measure),
      upload: currentValue(settings, keys.upload),
      align: currentValue(settings, "wake_align"),
      sched: currentValue(settings, "sched") ?? "",
    }),
    [settings, keys.measure, keys.upload],
  );
  const start = useMemo(() => rowsFrom(current.sched, tzHours), [current.sched, tzHours]);
  const [measure, setMeasure] = useState(null);
  const [upload, setUpload] = useState(null);
  const [align, setAlign] = useState(null);
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const shownRows = rows ?? start.rows;
  // One-offs in the past have run (or never will); they are dropped.
  const oneOffs = start.oneOffs.filter((e) => e.at > now / 1000);
  const sched = [
    ...shownRows.map((r) => dailyText(r, tzHours)),
    ...oneOffs.map((e) => e.text),
  ].join(";");
  const problem = shownRows.some((r) => !r.tasks.size) ? "every entry needs a task" : schedProblem(sched);
  const words = problem ? [] : describe(parseSched(sched), tzHours);
  const changes = {};
  if (measure !== null && measure !== current.measure) changes[keys.measure] = measure;
  if (upload !== null && upload !== current.upload) changes[keys.upload] = upload;
  if (align !== null && align !== (current.align ? 1 : 0)) changes.wake_align = align;
  if (rows !== null && sched !== current.sched) changes.sched = sched;
  const changed = Object.keys(changes).length > 0;
  const fw = String(sample?.raw?.fw || "");
  const has037 = /^0\.3\.(7|[89])|^0\.([4-9]|\d{2})\.|^[1-9]/.test(fw);

  const editRow = (id, patch) =>
    setRows(shownRows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const toggleDay = (r, d) => {
    const days = r.localDays === ALL_DAYS ? 1 << d : r.localDays ^ (1 << d);
    editRow(r.id, { localDays: days || ALL_DAYS });
  };
  const toggleTask = (r, c) => {
    const tasks = new Set(r.tasks);
    if (tasks.has(c)) tasks.delete(c);
    else tasks.add(c);
    if (c === "k" && tasks.has("k")) parseTasks("k").forEach((t) => tasks.add(t));
    if (c !== "k" && !tasks.has(c)) tasks.delete("k");
    editRow(r.id, { tasks });
  };
  const add = () =>
    setRows([
      ...shownRows,
      { id: Math.max(-1, ...shownRows.map((r) => r.id)) + 1, localMinute: 6 * 60 + 30, localDays: ALL_DAYS, tasks: new Set(["p"]) },
    ]);
  const apply = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const answer = await rtctApi.applySettings(mac, changes);
      setMessage({ ok: true, text: "Sent. The node picks it up at its next upload." });
      setMeasure(null);
      setUpload(null);
      setAlign(null);
      setRows(null);
      onApplied?.(answer);
    } catch (e) {
      setMessage({ ok: false, text: e?.message || "The settings could not be sent" });
    } finally {
      setBusy(false);
    }
  };

  const measureValue = measure ?? current.measure;
  const uploadValue = upload ?? current.upload;
  const presetOptions = (list, value) =>
    [...new Set([...list, ...(Number.isFinite(value) ? [value] : [])])].sort((a, b) => a - b);
  return (
    <section className="field-panel" aria-label="Schedule">
      <div className="field-panel-head">
        <div>
          <h2>Schedule</h2>
          <p>
            Times in UTC{tzHours >= 0 ? "+" : ""}
            {tzHours}, uploads over {cellular ? "cellular" : "Wi-Fi"}
          </p>
        </div>
        {settings?.status && <span className="field-state">{settings.status.replaceAll("_", " ")}</span>}
      </div>
      <div className="field-panel-body">
        <div className="field-node-row">
          <label>
            Measure every{" "}
            <select
              aria-label="Measure every"
              value={Number.isFinite(measureValue) ? measureValue : ""}
              onChange={(e) => setMeasure(Number(e.target.value))}
            >
              {!Number.isFinite(measureValue) && <option value="">not reported</option>}
              {presetOptions(MEASURE_PRESETS, measureValue).map((s) => (
                <option key={s} value={s}>
                  {presetText(s)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Upload{" "}
            <select
              aria-label="Upload"
              value={Number.isFinite(uploadValue) ? uploadValue : ""}
              onChange={(e) => setUpload(Number(e.target.value))}
            >
              {!Number.isFinite(uploadValue) && <option value="">not reported</option>}
              {presetOptions(UPLOAD_PRESETS, uploadValue).map((s) => (
                <option key={s} value={s}>
                  {s === 0 ? "every wake" : `every ${presetText(s)}`}
                </option>
              ))}
            </select>
          </label>
          <label>
            <input
              type="checkbox"
              checked={align ?? !!current.align}
              onChange={(e) => setAlign(e.target.checked ? 1 : 0)}
            />{" "}
            On the clock (:00, :15, …)
          </label>
        </div>
        <table className="field-table" aria-label="Timed entries">
          <thead>
            <tr>
              <th>Local time</th>
              <th>Days</th>
              <th>Tasks</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {shownRows.map((r) => (
              <tr key={r.id}>
                <td>
                  <input
                    type="time"
                    aria-label="Entry time"
                    value={minuteText(r.localMinute)}
                    onChange={(e) => {
                      const [h, m] = e.target.value.split(":").map(Number);
                      if (Number.isFinite(h) && Number.isFinite(m)) editRow(r.id, { localMinute: h * 60 + m });
                    }}
                  />
                </td>
                <td className="field-node-days">
                  {WEEKDAYS.map((d, i) => (
                    <button
                      type="button"
                      key={d}
                      className={`field-node-chip${r.localDays & (1 << i) ? " is-on" : ""}`}
                      aria-pressed={!!(r.localDays & (1 << i))}
                      onClick={() => toggleDay(r, i)}
                    >
                      {d.slice(0, 2)}
                    </button>
                  ))}
                </td>
                <td className="field-node-tasks">
                  {EDIT_TASKS.map((c) => (
                    <label key={c} title={TASK_NAMES[c]}>
                      <input type="checkbox" checked={r.tasks.has(c)} onChange={() => toggleTask(r, c)} /> {TASK_NAMES[c]}
                    </label>
                  ))}
                </td>
                <td>
                  <button
                    type="button"
                    className="field-button"
                    aria-label="Remove entry"
                    onClick={() => setRows(shownRows.filter((x) => x.id !== r.id))}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="field-actions">
          <button type="button" className="field-button" onClick={add}>
            Add a timed entry
          </button>
        </div>
        {oneOffs.length > 0 && (
          <p className="field-caption">
            {oneOffs.length} one-off entr{oneOffs.length === 1 ? "y" : "ies"} (satellite passes) kept as they are.
          </p>
        )}
        {problem ? (
          <div className="field-alert">Not allowed: {problem}.</div>
        ) : (
          words.length > 0 && (
            <ul className="field-node-words" aria-label="The schedule in words">
              {words.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )
        )}
        {!has037 && (
          <p className="field-caption">
            Timed entries and clock alignment need firmware 0.3.7; this node runs {fw || "an unknown version"} and
            ignores them until then.
          </p>
        )}
        <div className="field-actions">
          <button type="button" className="field-button primary" disabled={busy || !changed || !!problem} onClick={apply}>
            {changed ? "Apply" : "Nothing changed"}
          </button>
          {changed && (
            <button
              type="button"
              className="field-button"
              disabled={busy}
              onClick={() => {
                setMeasure(null);
                setUpload(null);
                setAlign(null);
                setRows(null);
              }}
            >
              Undo changes
            </button>
          )}
        </div>
        {message && (
          <div className={message.ok ? "field-notice" : "field-alert"} role="status">
            {message.text}
          </div>
        )}
      </div>
    </section>
  );
}
